import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SITE } from "@/constants/testIds";

// ---- Variant style map: replaces nested ternaries (lower complexity) ----
const VARIANT_STYLES = {
    primary:
        "bg-moss text-cream hover:bg-moss/90 shadow-[0_10px_24px_rgba(30,35,32,0.12)] hover:shadow-[0_14px_34px_rgba(30,35,32,0.16)]",
    accent:
        "bg-terracotta text-cream hover:bg-terracotta/90 shadow-[0_10px_24px_rgba(200,107,74,0.18)]",
    secondary:
        "bg-cream text-moss border border-border/80 hover:border-moss/40 hover:bg-sand/70",
    ghost: "text-moss hover:bg-sand/70",
};

const BASE_STYLES =
    "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream";

const DEFAULT_TEST_IDS = {
    primary: SITE.primaryButton,
    accent: SITE.primaryButton,
    secondary: SITE.secondaryButton,
    ghost: SITE.secondaryButton,
};

/**
 * Polymorphic CTA button.
 * - `to`     → renders <Link>
 * - `href`   → renders <a> (external if URL starts with http)
 * - neither  → renders <button>
 *
 * Visual variants come from a static lookup map (`VARIANT_STYLES`) instead of
 * nested ternaries, keeping cyclomatic complexity low and the styles easy to
 * extend.
 */
export const CtaButton = ({
    to,
    href,
    children,
    variant = "primary",
    icon = true,
    className = "",
    testid,
    onClick,
}) => {
    const variantClass = VARIANT_STYLES[variant] || VARIANT_STYLES.primary;
    const finalClassName = `${BASE_STYLES} ${variantClass} ${className}`.trim();
    const finalTestId = testid || DEFAULT_TEST_IDS[variant] || DEFAULT_TEST_IDS.primary;

    const content = (
        <>
            <span>{children}</span>
            {icon && <ArrowRight className="h-4 w-4" strokeWidth={1.75} />}
        </>
    );

    if (to) {
        return (
            <Link
                to={to}
                data-testid={finalTestId}
                onClick={onClick}
                className={finalClassName}
            >
                {content}
            </Link>
        );
    }

    if (href) {
        const isExternal = href.startsWith("http");
        return (
            <a
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noreferrer" : undefined}
                data-testid={finalTestId}
                onClick={onClick}
                className={finalClassName}
            >
                {content}
            </a>
        );
    }

    return (
        <button
            type="button"
            data-testid={finalTestId}
            onClick={onClick}
            className={finalClassName}
        >
            {content}
        </button>
    );
};

export default CtaButton;
